/**
 * Task Automation ("Tasks") — shared Zod API contract (BAL-7917).
 *
 * The client-side (Zod) mirror of the BE TypeBox contract
 * (`apps/unified-api/src/domains/workforce/routes/external/tasks.schema.ts`). Lives in
 * `@ballerine/common` so any FE
 * surface can import it; both sides source their string domains from the same `consts` arrays, so
 * the DB pgEnum, the BE contract, and this mirror can never drift. Read models + the write payloads
 * the client sends. No fetchers/hooks here (endpoint tickets: 7934 list, 7937 detail, 7939).
 *
 * See `docs/plans/BAL-7917-task-automation-backend.md` (§API surface).
 */

import {
  WORKFORCE_ACTOR_TYPES,
  WORKFORCE_AGENT_EXECUTIONS,
  WORKFORCE_CONDITION_TYPES,
  WORKFORCE_EVENTS,
  WORKFORCE_IDEMPOTENCY_SCOPES,
  WORKFORCE_RISK_TYPES,
  WORKFORCE_TASK_KINDS,
  WORKFORCE_TASK_RELATIONSHIPS,
  WORKFORCE_TASK_RESOLUTIONS,
  WORKFORCE_TASK_ROLES,
  WORKFORCE_TASK_RULE_MATCHES,
  WORKFORCE_TASK_STATUSES,
} from '../consts';
import type { RulesLogic } from 'json-logic-js';
import { z } from 'zod';

// ============================================================================
// Enums — sourced from @ballerine/common (single contract source)
// ============================================================================

export const WorkforceTaskStatusSchema = z.enum(WORKFORCE_TASK_STATUSES);
export const WorkforceTaskResolutionSchema = z.enum(WORKFORCE_TASK_RESOLUTIONS);
export const WorkforceTaskKindSchema = z.enum(WORKFORCE_TASK_KINDS);
export const WorkforceActorTypeSchema = z.enum(WORKFORCE_ACTOR_TYPES);
export const WorkforceRiskTypeSchema = z.enum(WORKFORCE_RISK_TYPES);
export const WorkforceEventSchema = z.enum(WORKFORCE_EVENTS);
export const WorkforceTaskRelationshipSchema = z.enum(WORKFORCE_TASK_RELATIONSHIPS);
export const WorkforceIdempotencyScopeSchema = z.enum(WORKFORCE_IDEMPOTENCY_SCOPES);
export const WorkforceConditionTypeSchema = z.enum(WORKFORCE_CONDITION_TYPES);
export const WorkforceAgentExecutionSchema = z.enum(WORKFORCE_AGENT_EXECUTIONS);
export const WorkforceTaskRuleMatchSchema = z.enum(WORKFORCE_TASK_RULE_MATCHES);
export const WorkforceTaskRoleSchema = z.enum(WORKFORCE_TASK_ROLES);

// ============================================================================
// Shared value objects
// ============================================================================

/** Polymorphic actor (B12/F17/F19) — `id` is null for the `system` actor. */
export const WorkforceActorSchema = z.object({
  type: WorkforceActorTypeSchema,
  id: z.string().nullable(),
});

export const WorkforceTaskAssociationSchema = z.object({
  type: z.string(),
  id: z.string(),
});

/** `emit` (Rev-3, K36) — a rule emits only its ENTRY definition(s). No `ref`/`role`/`relations`: the
 *  parent→child topology lives in the def-graph (`workforce_task_def_links`), cloned at publish. */
export const WorkforceEmitTaskSchema = z.object({
  definitionId: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export const WorkforceEmitSchema = z.object({
  tasks: z.array(WorkforceEmitTaskSchema),
});

/** Δ36 — the whole rule logic in one object: the JsonLogic boolean + the declarative emit + eval strategy. */
export const WorkforceRuleLogicSchema = z.object({
  // #23 — `when` is a JsonLogic expression (`true` = always fire), typed as `RulesLogic`. `z.custom`
  // carries the type but does NOT validate at runtime. That's fine while rules are SEED-ONLY (we
  // author them). TODO(rule-authoring-api): before any UNTRUSTED write path (a customer/admin rule
  // API), add runtime validation — an operator allowlist, a nesting-depth cap, and a reject of
  // `__proto__`/`constructor` in `var` paths (json-logic-js's `var` walks object paths).
  when: z.custom<RulesLogic>(),
  emit: WorkforceEmitSchema,
  match: WorkforceTaskRuleMatchSchema.optional(), // default 'all'
  priority: z.number().optional(), // default 0; in-memory ordering for 'first'
});

// ============================================================================
// Task — read model
// ============================================================================

export const WorkforceTaskSchema = z.object({
  id: z.string(),
  customerId: z.string(),

  definitionId: z.string().nullable(),
  triggerRuleId: z.string().nullable(),
  // `event` lives on the fire (workforce_task_fires), not the task — so it's not on the task shape.
  sourceReportId: z.string().nullable(),
  // NOTE: no reportSeriesId — Δ39 (migration 0234) dropped workforce_tasks.report_series_id; the series
  // is derived on read via source_report_id → business_analysis.report_series_id. Kept in lockstep with
  // the TypeBox WorkforceTaskSchema (BE⇄FE parity contract test).
  businessId: z.string().nullable(),
  riskType: WorkforceRiskTypeSchema.nullable(),
  associations: z.array(WorkforceTaskAssociationSchema),

  teamId: z.string().nullable(),
  assignee: WorkforceActorSchema.nullable(),
  // The human sign-off slot (BAL-8059; "the reviewer will be the owner"). Always a user id.
  ownerId: z.string().nullable(),

  title: z.string(),
  instructions: z.string().nullable(),
  taskKind: WorkforceTaskKindSchema,
  role: WorkforceTaskRoleSchema, // D3

  status: WorkforceTaskStatusSchema,
  resolution: WorkforceTaskResolutionSchema.nullable(),
  resolutionReason: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  output: z.record(z.string(), z.unknown()).nullable(), // written at completion (L5)

  createdBy: WorkforceActorSchema,
  closedBy: WorkforceActorSchema.nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TWorkforceTask = z.infer<typeof WorkforceTaskSchema>;

export const WorkforceTaskLinkSchema = z.object({
  id: z.string(),
  sourceTaskId: z.string(),
  targetTaskId: z.string(),
  relationship: WorkforceTaskRelationshipSchema,
  createdBy: WorkforceActorSchema.nullable(),
  createdByRuleId: z.string().nullable(),
  createdAt: z.string(),
});

export const WorkforceTaskAttachmentSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  commentId: z.string().nullable(),
  fileId: z.string(), // workflows-service File id — the FE mints a signed download URL from it
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  sha256: z.string().nullable(),
  uploadedBy: WorkforceActorSchema.nullable(), // actor pair (L1)
  createdAt: z.string(),
});

export const WorkforceTaskCommentSchema = z.object({
  id: z.string(),
  // [Δ38] Generic polymorphic subject — no task FK. Phase-1: subjectType='workforce_task'.
  subjectType: z.string(),
  subjectId: z.string(),
  author: WorkforceActorSchema,
  body: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// ============================================================================
// GET /tasks — list (filters + cursor; flat or grouped)
// ============================================================================

const DATE_BOUND_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Filter fields shared by /tasks/list and /tasks/summary (BAL-7934 Phase 2) — mirrors the
 *  BE's TASK_FILTER_FIELDS so header counts always accept the same filters as the rows. */
/** The `team` filter's unassigned/triage sentinel (`team_id IS NULL` server-side) — shared so
 *  the FE dropdown and the BE condition can never drift on the magic string. */
export const UNASSIGNED_TEAM_ID = 'none';

const taskFilterFields = {
  team: z.string().optional(), // team id, or UNASSIGNED_TEAM_ID for unassigned/triage
  status: z.array(WorkforceTaskStatusSchema).optional(),
  resolution: z.array(WorkforceTaskResolutionSchema).optional(),
  assigneeId: z.string().optional(),
  assigneeKind: z.enum(['agent', 'human']).optional(), // 'human' includes unassigned
  ownerId: z.string().min(1).optional(), // reviewer / sign-off owner
  businessId: z.string().optional(),
  sourceReportId: z.string().optional(),
  search: z.string().max(256).optional(), // case-insensitive over title + merchant name
  from: z.string().regex(DATE_BOUND_REGEX).optional(), // created-at lower bound, YYYY-MM-DD UTC
  to: z.string().regex(DATE_BOUND_REGEX).optional(), // created-at upper bound, inclusive day
} as const;

export const GetTasksQuerySchema = z.object({
  ...taskFilterFields,
  grouped: z.boolean().optional(),
  cursor: z.string().optional(),
  limit: z.number().min(1).max(200).optional(),
});
export type TGetTasksQuery = z.infer<typeof GetTasksQuerySchema>;

export const GetTasksSummaryQuerySchema = z.object(taskFilterFields);
export type TGetTasksSummaryQuery = z.infer<typeof GetTasksSummaryQuerySchema>;

export const WorkforceTaskGroupSchema = z.object({
  parent: WorkforceTaskSchema,
  children: z.array(WorkforceTaskSchema),
  readyForResolution: z.boolean(),
  hasChildren: z.boolean(),
});

export const GetTasksResponseSchema = z.object({
  data: z.array(WorkforceTaskSchema),
  groups: z.array(WorkforceTaskGroupSchema).optional(),
  nextCursor: z.string().nullable(),
});
export type TGetTasksResponse = z.infer<typeof GetTasksResponseSchema>;
export type TWorkforceTaskGroup = z.infer<typeof WorkforceTaskGroupSchema>;

// ============================================================================
// GET /tasks/summary — counts
// ============================================================================

const CountByKeySchema = z.array(z.object({ key: z.string(), count: z.number() }));
export const GetTasksSummaryResponseSchema = z.object({
  byTeam: CountByKeySchema,
  byResolution: CountByKeySchema,
  byStatus: CountByKeySchema,
  reportsWithOpenTasks: z.number(),
  totalOpen: z.number(),
  // Agent-held slice of byStatus.in_progress ("AI Working" band). Optional so an FE deployed
  // ahead of the BE degrades gracefully (band shows 0 instead of NaN).
  inProgressAiCount: z.number().optional(),
});
export type TGetTasksSummaryResponse = z.infer<typeof GetTasksSummaryResponseSchema>;

// ============================================================================
// GET /teams — the customer's teams (Team filter options, BAL-7934 Phase 2)
// ============================================================================

export const GetTeamsResponseSchema = z.object({
  data: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string().nullable() })),
});
export type TGetTeamsResponse = z.infer<typeof GetTeamsResponseSchema>;

// ============================================================================
// GET /tasks/:id — detail (task + unified hydrated activity feed + links + attachments)
// ============================================================================

export const WorkforceActivityItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  actor: WorkforceActorSchema,
  createdAt: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  comment: WorkforceTaskCommentSchema.optional(),
});

/** Display hydration for the detail header/context (BAL-7937) — additive, optional. */
export const TaskDetailBusinessSchema = z.object({
  id: z.string(),
  companyName: z.string().nullable(),
});
export const TaskDetailSourceReportSchema = z.object({
  id: z.string(),
  reportType: z.string().nullable(),
});

export const GetTaskDetailResponseSchema = z.object({
  task: WorkforceTaskSchema,
  links: z.array(WorkforceTaskLinkSchema),
  attachments: z.array(WorkforceTaskAttachmentSchema),
  activity: z.array(WorkforceActivityItemSchema),
  // --- Additive hydration (BAL-7937; optional, BC-safe) ---
  // Tasks referenced by `links` plus siblings (other children of the same parent), hydrated so the
  // detail page can render the non-blocking "other team also required" banner without extra round-trips.
  linkedTasks: z.array(WorkforceTaskSchema).optional(),
  business: TaskDetailBusinessSchema.optional(), // header subtitle
  sourceReport: TaskDetailSourceReportSchema.optional(), // monitoring-alert link
});
export type TGetTaskDetailResponse = z.infer<typeof GetTaskDetailResponseSchema>;

// ============================================================================
// POST /tasks — manual creation from the UI (BAL-7934) + parent/subtask linking (BAL-8058)
// ============================================================================

/** Mirrors the BE TypeBox `CreateTaskBodySchema` — a user-authored manual task (origin `manual`). */
export const CreateTaskBodySchema = z.object({
  title: z.string().min(1).max(256),
  instructions: z.string().max(10_000).optional(),
  businessId: z.string().min(1).optional(),
  sourceReportId: z.string().min(1).optional(),
  role: z.enum(['parent', 'standalone']).optional(),
  parentTaskId: z.string().min(1).optional(),
  assignee: WorkforceActorSchema.nullable().optional(),
  ownerId: z.string().nullable().optional(),
});
export type TCreateTaskBody = z.infer<typeof CreateTaskBodySchema>;

/** POST /tasks response — the created task (mirrors the BE TypeBox schema). */
export const CreateTaskResponseSchema = z.object({
  task: WorkforceTaskSchema,
});
export type TCreateTaskResponse = z.infer<typeof CreateTaskResponseSchema>;

// ============================================================================
// PATCH /tasks/:id — resolve / transition / assign / reopen
// ============================================================================

/**
 * Server-enforced: `done` on a `requires_resolution` task needs `resolution` + `resolutionReason`;
 * `cancelled` needs `cancellationReason` (B8). All fields optional so one endpoint drives
 * status/assign/reopen.
 */
export const PatchTaskBodySchema = z.object({
  status: WorkforceTaskStatusSchema.optional(),
  resolution: WorkforceTaskResolutionSchema.optional(),
  resolutionReason: z.string().optional(),
  cancellationReason: z.string().optional(),
  teamId: z.string().nullable().optional(),
  assignee: WorkforceActorSchema.nullable().optional(),
  ownerId: z.string().nullable().optional(), // human sign-off slot (E18-validated)
});
export type TPatchTaskBody = z.infer<typeof PatchTaskBodySchema>;

/** PATCH /tasks/:id response — the updated task (mirrors the BE TypeBox schema). */
export const PatchTaskResponseSchema = z.object({
  task: WorkforceTaskSchema,
});

// ============================================================================
// Comments · Links · Attachments (write payloads the FE sends)
// ============================================================================

/**
 * One already-uploaded WF file's metadata. The bytes land in the workflows-service File store
 * first; this is what names them to unified-api — either alongside a note (BAL-8168) or on its
 * own via POST /tasks/:id/attachments.
 */
export const TaskAttachmentInputSchema = z.object({
  fileId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  sha256: z.string().optional(),
});
export type TTaskAttachmentInput = z.infer<typeof TaskAttachmentInputSchema>;

export const CreateTaskCommentBodySchema = z.object({
  body: z.string().min(1),
  /**
   * Files staged in the composer and submitted together with the note (BAL-8168) — the comment,
   * its attachment rows and the audit entry commit in one transaction, so a failure leaves no
   * half-written activity behind.
   */
  attachments: z.array(TaskAttachmentInputSchema).optional(),
  // Shipped with BAL-7937; unused by the current comment path (attachments register via
  // POST /tasks/:id/attachments). Kept for backwards compatibility with existing consumers.
  attachmentFileIds: z.array(z.string()).optional(),
});

/** POST /tasks/:id/comments response — the created note (mirrors the BE TypeBox schema). */
export const CreateTaskCommentResponseSchema = z.object({
  comment: WorkforceTaskCommentSchema,
});

export type TCreateTaskCommentResponse = z.infer<typeof CreateTaskCommentResponseSchema>;

export const CreateTaskLinkBodySchema = z.object({
  targetTaskId: z.string(),
  relationship: WorkforceTaskRelationshipSchema,
});

// ----------------------------------------------------------------------------
// Attachment upload policy (BAL-7939) — single source for the FE pre-check
// (`accept` attribute + size guard) and the BE validation (authoritative).
// ----------------------------------------------------------------------------

export const TASK_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * Allowed extensions → the declared content-types accepted for each. A strict
 * subset of browser-servable evidence types — svg is deliberately excluded
 * (script-in-SVG XSS when rendered inline).
 */
export const TASK_ATTACHMENT_ALLOWED_TYPES: Record<string, readonly string[]> = {
  pdf: ['application/pdf'],
  png: ['image/png'],
  jpg: ['image/jpeg', 'image/jpg'],
  jpeg: ['image/jpeg', 'image/jpg'],
  gif: ['image/gif'],
  txt: ['text/plain'],
  csv: ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  xls: ['application/vnd.ms-excel'],
};

/** `<input accept>` value derived from the allowlist. */
export const TASK_ATTACHMENT_ACCEPT = Object.keys(TASK_ATTACHMENT_ALLOWED_TYPES)
  .map(extension => `.${extension}`)
  .join(',');

// `workforce_task_attachments.filename` is varchar(512).
const TASK_ATTACHMENT_FILENAME_MAX_LENGTH = 512;

const taskAttachmentExtensionOf = (filename: string): string =>
  filename.includes('.') ? filename.split('.').at(-1)!.toLowerCase() : '';

// ----------------------------------------------------------------------------
// Agent evidence policy (BAL-8181) — a SUPERSET of the human policy, consumed ONLY by
// the unified-api agent evidence lane. Deliberately a second policy rather than a wider
// shared one: the human list also drives the backoffice `accept` attribute, and these
// agents are pointed at scam/fraud sites by design — an unbounded store would render a
// merchant-served binary as a download button in front of an analyst. Executables stay
// out; when the allowlist blocks something genuinely needed the failure is loud and a
// one-line change away.
// ----------------------------------------------------------------------------

/** 100 MB — already unified-api's `@fastify/multipart` default, under WF's 200 MB relay cap. */
export const AGENT_EVIDENCE_MAX_BYTES = 100 * 1024 * 1024;

/** Kinds only an agent may store, on top of every human-allowed type. */
const AGENT_ONLY_EVIDENCE_TYPES: Record<string, readonly string[]> = {
  md: ['text/markdown', 'text/x-markdown', 'text/plain'],
  html: ['text/html'],
  json: ['application/json', 'text/json', 'text/plain'],
  mp4: ['video/mp4'],
  webm: ['video/webm'],
  mov: ['video/quicktime'],
  zip: ['application/zip', 'application/x-zip-compressed'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};

export const AGENT_EVIDENCE_ALLOWED_TYPES: Record<string, readonly string[]> = {
  ...TASK_ATTACHMENT_ALLOWED_TYPES,
  ...AGENT_ONLY_EVIDENCE_TYPES,
};

/**
 * The agent-evidence relay's OWN extension gate (BAL-8181 finding 1) — DERIVED from
 * `AGENT_EVIDENCE_ALLOWED_TYPES`'s keys, never a hand-kept second enumeration. This is the
 * regex `services/workflows-service/src/storage/agent-evidence-file-filter.ts`'s multer
 * `fileFilter` imports for the dedicated `POST /internal/storage/agent-evidence` route —
 * deliberately NOT `SUPPORTED_FILE_EXT_REGEX` (`consts/index.ts`), which every human/
 * customer-facing upload surface also imports and which the human policy's own extensions
 * (`TASK_ATTACHMENT_ALLOWED_TYPES`) still map to. Widening THAT shared regex would silently
 * widen what an analyst can upload through the browser too. Because this regex is derived
 * rather than enumerated, the relay's actual multer gate and this claim-validation allowlist
 * cannot drift apart — pinned by `agent-evidence-relay-contract.unit.test.ts`.
 */
export const AGENT_EVIDENCE_FILE_EXT_REGEX = new RegExp(
  `\\.(${Object.keys(AGENT_EVIDENCE_ALLOWED_TYPES).join('|')})$`,
  'i',
);

type AttachmentClaim = {
  filename: string;
  contentType: string;
  sizeBytes: number;
};

type AttachmentPolicy = {
  allowedTypes: Record<string, readonly string[]>;
  maxBytes: number;
};

/**
 * The ONE claim-validation implementation, parameterised by policy (architecture rule:
 * never a second copy of a business rule). An empty `contentType` skips the
 * mime↔extension consistency check — some OS/browser combos report no mime for csv/xlsx;
 * the authoritative path (workflows-service) derives one from the file before registering.
 */
const validateAttachmentClaim = (
  { filename, contentType, sizeBytes }: AttachmentClaim,
  { allowedTypes, maxBytes }: AttachmentPolicy,
): string | null => {
  if (filename.length === 0 || filename.length > TASK_ATTACHMENT_FILENAME_MAX_LENGTH) {
    return 'Attachment filename is missing or too long';
  }

  const allowedContentTypes = allowedTypes[taskAttachmentExtensionOf(filename)];

  if (!allowedContentTypes) {
    return 'Attachment file type is not supported';
  }

  if (contentType && !allowedContentTypes.includes(contentType.toLowerCase())) {
    return 'Attachment content type does not match its file extension';
  }

  // Empty and oversized are OPPOSITE failures and must not share a message. They did, and the
  // agent lane is a machine reader: a JS-rendered page (Notion, any SPA shell) strips to zero
  // text, `http_fetch` uploaded the 0-byte result, and the model was told "exceeds the 100 MB
  // size limit" — which it then wrote into its verdict as the reason the Privacy and Terms
  // evidence was missing. A wrong rejection reason becomes a wrong finding (live, 2026-08-09).
  if (sizeBytes <= 0) {
    return 'Attachment is empty';
  }

  if (sizeBytes > maxBytes) {
    return `Attachment exceeds the ${maxBytes / 1024 / 1024} MB size limit`;
  }

  return null;
};

/**
 * The single task-attachment validation shared by the FE pre-check and unified-api's
 * register-time check (one implementation, no drift). Returns null when valid; a static
 * human-readable rejection reason otherwise.
 */
export const validateTaskAttachment = (claim: AttachmentClaim): string | null =>
  validateAttachmentClaim(claim, {
    allowedTypes: TASK_ATTACHMENT_ALLOWED_TYPES,
    maxBytes: TASK_ATTACHMENT_MAX_BYTES,
  });

/** The agent-lane claim check (BAL-8181) — superset allowlist, 100 MB cap. */
export const validateAgentEvidence = (claim: AttachmentClaim): string | null =>
  validateAttachmentClaim(claim, {
    allowedTypes: AGENT_EVIDENCE_ALLOWED_TYPES,
    maxBytes: AGENT_EVIDENCE_MAX_BYTES,
  });

/** Register an already-uploaded WF file as a task attachment (mirrors the BE TypeBox schema). */
export const RegisterTaskAttachmentBodySchema = TaskAttachmentInputSchema;
export type TRegisterTaskAttachmentBody = z.infer<typeof RegisterTaskAttachmentBodySchema>;

/**
 * POST /storage/workforce-tasks/:taskId/files response (BAL-8168) — workflows-service stored the
 * bytes and named the file; nothing is registered against the task until the note is submitted.
 */
export const UploadTaskAttachmentFileResponseSchema = TaskAttachmentInputSchema;
export type TUploadTaskAttachmentFileResponse = z.infer<
  typeof UploadTaskAttachmentFileResponseSchema
>;

/** POST /tasks/:id/attachments response — the registered attachment (mirrors the BE TypeBox schema). */
export const CreateTaskAttachmentResponseSchema = z.object({
  attachment: WorkforceTaskAttachmentSchema,
});
export type TCreateTaskAttachmentResponse = z.infer<typeof CreateTaskAttachmentResponseSchema>;

// ============================================================================
// Definitions (task shape) + Rules (condition + emit) — admin authoring
// ============================================================================

export const WorkforceTaskDefinitionSchema = z.object({
  id: z.string(),
  customerId: z.string().nullable(), // null = public catalog template (K25)
  name: z.string(),
  key: z.string().nullable(), // stable slug — tasks.* addressing key (L5)
  description: z.string().nullable(),
  sourceDefinitionId: z.string().nullable(), // lineage of a catalog copy (K25)
  teamId: z.string().nullable(),
  taskKind: WorkforceTaskKindSchema,
  defaultTitle: z.string(),
  defaultInstructions: z.string().nullable(),
  riskType: WorkforceRiskTypeSchema.nullable(),
  agentExecution: WorkforceAgentExecutionSchema,
  createdBy: WorkforceActorSchema.nullable(), // D7
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpsertTaskDefinitionBodySchema = z.object({
  name: z.string().min(1),
  key: z.string().optional(),
  description: z.string().optional(),
  teamId: z.string().nullable().optional(),
  taskKind: WorkforceTaskKindSchema.optional(),
  defaultTitle: z.string().min(1),
  defaultInstructions: z.string().optional(),
  riskType: WorkforceRiskTypeSchema.optional(),
  agentExecution: WorkforceAgentExecutionSchema.optional(),
  isActive: z.boolean().optional(),
});

export const WorkforceTaskRuleSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  event: WorkforceEventSchema,
  reportTypes: z.array(z.string()).nullable(), // NULLABLE (D1)
  condition: WorkforceRuleLogicSchema, // Δ36 — folded { when, emit, match?, priority? }
  conditionType: WorkforceConditionTypeSchema,
  idempotencyScope: WorkforceIdempotencyScopeSchema,
  createdBy: WorkforceActorSchema.nullable(), // D7
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpsertTaskRuleBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  event: WorkforceEventSchema.optional(),
  reportTypes: z.array(z.string().min(1)).optional(),
  condition: WorkforceRuleLogicSchema, // Δ36 — folded { when, emit, match?, priority? }
  conditionType: WorkforceConditionTypeSchema.optional(),
  idempotencyScope: WorkforceIdempotencyScopeSchema.optional(),
  isActive: z.boolean().optional(),
});

/** POST /tasks/rules/:id/simulate — dry-run the graph without writing. */
export const SimulateTaskRuleBodySchema = z.object({
  context: z.record(z.string(), z.unknown()).optional(),
  sourceReportId: z.string().optional(),
});
export const SimulateTaskRuleResponseSchema = z.object({
  willFire: z.boolean(),
  // Rev-3: the resolved family is the cloned def-graph subtree — role is structural, links are edges.
  tasks: z.array(
    z.object({
      definitionId: z.string(),
      title: z.string(),
      role: WorkforceTaskRoleSchema,
    }),
  ),
  links: z.array(
    z.object({
      sourceDefinitionId: z.string(),
      targetDefinitionId: z.string(),
      relationship: WorkforceTaskRelationshipSchema,
    }),
  ),
});

// ============================================================================
// GET /tasks/reports/:reportId/automation — per-report overlay payload
// ============================================================================

export const ReportAutomationRowSchema = z.object({
  riskType: WorkforceRiskTypeSchema.nullable(),
  taskId: z.string(),
  finding: z.string(),
  status: WorkforceTaskStatusSchema,
  resolution: WorkforceTaskResolutionSchema.nullable(),
  effectiveStatus: z.string(),
  resolvedBy: WorkforceActorSchema.nullable(),
  resolvedAt: z.string().nullable(),
  reason: z.string().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
});

export const GetReportAutomationResponseSchema = z.object({
  reportId: z.string(),
  readyForResolution: z.boolean(),
  rows: z.array(ReportAutomationRowSchema),
});

/** Light per-report task summary carried by the reports-list endpoint (J17/B6). */
// A projection of the canonical `WorkforceTaskSchema` (SSOT) — mirrors the TypeBox `T.Pick`
// in unified-api's tasks.schema.ts so the summary can't drift from the full task shape.
export const ReportTaskSummaryItemSchema = WorkforceTaskSchema.pick({
  id: true,
  title: true,
  status: true,
  resolution: true,
  assignee: true,
  taskKind: true,
  role: true,
  riskType: true,
  createdAt: true,
  // Owner (BAL-8059) — the connected-tasks card renders the owner's avatar with the AI
  // sparkle badge when the assignee is an agent.
  ownerId: true,
});
export type TReportTaskSummaryItem = z.infer<typeof ReportTaskSummaryItemSchema>;

/**
 * Per-report task aggregate carried by the reports-list `data.tasks` include (BAL-8357).
 * `completedCount` counts terminal tasks (done + cancelled); `items` is the first 4 tasks
 * (open-first status rank, then createdAt) for the Tasks-column tooltip. The BE emits null
 * instead of this object when the report has no tasks.
 */
export const ReportTasksAggregateSchema = z.object({
  totalCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  items: z.array(ReportTaskSummaryItemSchema),
});
export type TReportTasksAggregate = z.infer<typeof ReportTasksAggregateSchema>;

/** GET /tasks?sourceReportId= querystring — the report id is required (BAL-8047). */
export const GetReportTasksQuerySchema = z.object({
  sourceReportId: z.string().min(1),
});
export type TGetReportTasksQuery = z.infer<typeof GetReportTasksQuerySchema>;

/** GET /tasks?sourceReportId= — the report's connected-tasks panel (BAL-8047). */
export const GetReportTasksResponseSchema = z.object({
  data: z.array(ReportTaskSummaryItemSchema),
});
export type TGetReportTasksResponse = z.infer<typeof GetReportTasksResponseSchema>;
